import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BetaFeatureService } from '../services/betaFeatureService';
import { RootState } from '../redux/reducers';
import useSelectedCommunity from './community/useSelectedCommunity';

/**
 * Dynamically map a feature key to its BetaFeature enum value
 */
const getBetaFeatureFromKey = (key: string): string | null => {
  return key;
};

/**
 * Custom hook to check if the current community has access to a beta feature
 * @param feature The beta feature name (e.g., 'financial', 'sales')
 * @returns boolean indicating if the community has access to the feature
 */
export function useBetaFeature(feature: string): boolean {
  const [hasAccess, setHasAccess] = useState(false);
  const community = useSelectedCommunity();
  const role = useSelector((state: RootState) => state.application.role);

  useEffect(() => {
    const checkAccess = async () => {
      if (role && role === 'ADMIN') {
        setHasAccess(true);
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        setHasAccess(true);
        return;
      }

      if (!community || community.id === undefined) {
        setHasAccess(false);
        return;
      }

      const betaFeature = getBetaFeatureFromKey(feature);
      if (!betaFeature) {
        setHasAccess(false);
        return;
      }

      try {
        const access = await BetaFeatureService.hasAccess(community.id, betaFeature);
        setHasAccess(access);
      } catch (error) {
        console.error('Error checking beta feature access:', error);
        setHasAccess(false);
      }
    };

    checkAccess();
  }, [feature, community?.id, role]);

  return hasAccess;
}

/**
 * Custom hook to check access for multiple beta features at once
 * @param features Array of beta feature keys to check
 * @returns Record mapping feature keys to their access status
 */
export function useBetaFeatures(features: string[]): Record<string, boolean> {
  const [accessMap, setAccessMap] = useState<Record<string, boolean>>({});
  const community = useSelectedCommunity();
  const role = useSelector((state: RootState) => state.application.role);

  useEffect(() => {
    const checkAllAccess = async () => {
      if (role && role === 'ADMIN') {
        setAccessMap(features.reduce((acc, feature) => ({ ...acc, [feature]: true }), {}));
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        setAccessMap(features.reduce((acc, feature) => ({ ...acc, [feature]: true }), {}));
        return;
      }

      if (!community || community.id === undefined) {
        const emptyMap: Record<string, boolean> = {};
        features.forEach((key) => { emptyMap[key] = false; });
        setAccessMap(emptyMap);
        return;
      }

      const results: Record<string, boolean> = {};
      
      await Promise.all(
        features.map(async (featureKey) => {
          const betaFeature = getBetaFeatureFromKey(featureKey);
          if (!betaFeature) {
            results[featureKey] = false;
            return;
          }

          try {
            const access = await BetaFeatureService.hasAccess(community.id, betaFeature);
            results[featureKey] = access;
          } catch (error) {
            console.error(`Error checking beta feature access for ${featureKey}:`, error);
            results[featureKey] = false;
          }
        })
      );

      setAccessMap(results);
    };

    checkAllAccess();
  }, [features.join(','), community?.id, role]);

  return accessMap;
}
